import { useLogger } from "@bindery/logging/react";
import {
  createContext,
  use,
  useCallback,
  useState,
  type ReactNode,
} from "react";
import { DEFAULT_DESIGN_SLUG } from "@/lib/utils/design-slug";
import { readDesignCookie, writeDesignCookie } from "./design-cookie";
import type { NavPalette } from "./nav-palette";
import { getNavPalette } from "./palettes";

interface DesignContextValue {
  activeSlug: string;
  setActiveSlug: (slug: string) => void;
}

const DesignContext = createContext<DesignContextValue | null>(null);

export function DesignProvider({ children }: { children: ReactNode }) {
  const [activeSlug, setActiveSlugState] = useState(() =>
    typeof document === "undefined"
      ? DEFAULT_DESIGN_SLUG
      : readDesignCookie(document.cookie),
  );

  const log = useLogger();
  const setActiveSlug = useCallback(
    (slug: string) => {
      if (!writeDesignCookie(slug)) {
        log.event("web.design.rejected", { slug });
        return;
      }
      setActiveSlugState(slug);
      log.event("web.design.updated", { slug });
    },
    [log],
  );

  return (
    <DesignContext value={{ activeSlug, setActiveSlug }}>
      {children}
    </DesignContext>
  );
}

export function useDesign(): DesignContextValue {
  const ctx = use(DesignContext);
  if (!ctx) {
    throw new Error("useDesign must be used within a DesignProvider");
  }
  return ctx;
}

export function useDesignPalette(): NavPalette {
  const { activeSlug } = useDesign();
  return getNavPalette(activeSlug);
}
